import { Component, Input, Signal } from '@angular/core';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { LenientDatePipe } from 'src/app/shared/pipes/lenient-date.pipe';
import { PhonePipe } from 'src/app/shared/pipes/phone.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { DelegationAddEditDialogComponent } from 'src/app/delegations/delegation-add-edit-dialog/delegation-add-edit-dialog.component';
import { DelegationSetInactiveDialogComponent } from 'src/app/delegations/delegation-set-inactive-dialog/delegation-set-inactive-dialog.component';
import { Delegation, DelegationAddEdit } from 'src/app/delegations/models/delegation';
import { DelegationUser } from 'src/app/delegations/models/delegation-user';
import { updateDelegation, setDelegationToInactive } from 'src/app/delegations/store/actions/delegations.actions';
import { DelegationsState } from 'src/app/delegations/store/delegations.state';
import { AuthService } from 'src/app/core/auth.service';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { DelegationNoTimeSelectedDefault, DelegationType } from '../models/delegation-type.enum';
import dayjs from 'dayjs';


@UntilDestroy()
@Component({
    selector: 'nova-delegation-card',
    standalone: true,
    imports: [
        MatDialogModule,
        MatIconModule,
        MatDividerModule,
        MatButtonModule,
        LenientDatePipe,
        PhonePipe,
        NgIf,
        NgFor,
        DatePipe
    ],
    templateUrl: './delegation-card.component.html',
    styleUrls: ['./delegation-card.component.scss']
})
export class DelegationCardComponent {
    @Input()
    delegation: Delegation;

    @Input()
    aspNetUserId: string;

    @Input()
    condensedView = false;

    isContactInfoHidden = true;

    delegationTypeEnum = DelegationType;
    auth0NovaImpersonationSignal: Signal<boolean>;
    auth0NovaLinkedProfilesSignal: Signal<boolean>;
    novaDelegationTimeframeSignal: Signal<boolean>;
    novaDelegationIsCurrentDelegateUtcFixSignal: Signal<boolean>;

    constructor(
        private readonly _store: Store<DelegationsState>,
        private readonly _authService: AuthService,
        private readonly _dialog: MatDialog,
        private readonly _ldFeatureManager: LDFeatureManager
    ) {
        this.auth0NovaLinkedProfilesSignal = this._ldFeatureManager.isEnabledSignal(
            FeatureFlag.Auth0NovaLinkedProfiles
        );
        this.novaDelegationTimeframeSignal = this._ldFeatureManager.isEnabledSignal(
            FeatureFlag.NovaDelegationTimeframe
        );
        this.auth0NovaImpersonationSignal = this._ldFeatureManager.isEnabledSignal(FeatureFlag.Auth0NovaImpersonation);
        this.novaDelegationIsCurrentDelegateUtcFixSignal = this._ldFeatureManager.isEnabledSignal(FeatureFlag.NovaDelegationIsCurrentDelegateUtcFix);
    }

    get isDelegationOwner(): boolean {
        return this.delegation.ownerAspNetUserId === this.aspNetUserId;
    }

    get delegationUser(): DelegationUser {
        return this.isDelegationOwner ? this.delegation.delegatedUser : this.delegation.delegatingUser;
    }

    get hasDelegationStartTime(): boolean {
        if (this.novaDelegationTimeframeSignal() && !this.dateTimesAreDefaulted() && this.delegation.delegationEndDate != null){
            return true;
        } else if (this.novaDelegationTimeframeSignal() && !this.isStartDateTimeDefaulted() && this.delegation.delegationEndDate == null){
            return true;
        }
        return false;
    }

    get hasDelegationEndTime(): boolean {
        return this.novaDelegationTimeframeSignal() && !this.dateTimesAreDefaulted();
    }

    isStartDateTimeDefaulted(): boolean {
        const startDate = dayjs(`${this.delegation.delegationStartDate}Z`);
        return startDate.hour() === DelegationNoTimeSelectedDefault.StartHour
            && startDate.minute() === DelegationNoTimeSelectedDefault.StartMinute;
    }

    isEndDateTimeDefaulted(): boolean {
        const endDate = dayjs(`${this.delegation.delegationEndDate}Z`);
        return endDate.hour() === DelegationNoTimeSelectedDefault.EndHour 
            && endDate.minute() === DelegationNoTimeSelectedDefault.EndMinute;
    }

    dateTimesAreDefaulted() {
        if (this.delegation.delegationEndDate != null){
            return this.isEndDateTimeDefaulted() && this.isStartDateTimeDefaulted();
        }
        return false;
    }

    cancelDelegation() {
        const dialog = this._dialog.open(DelegationSetInactiveDialogComponent, {
            panelClass: ['nova__dialog', 'nova__dialog--md'],
            disableClose: false,
            data: {
                isCurrent: this.delegation.isCurrent
            }
        });

        dialog
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((setInactive: boolean) => {
                if (setInactive) {
                    this._store.dispatch(setDelegationToInactive({ delegation: this.delegation }));
                }
            });
    }

    editCoverage() {
        const dialog = this._dialog.open(DelegationAddEditDialogComponent, {
            panelClass: ['nova__dialog', 'nova__dialog--md'],
            disableClose: false,
            data: {
                delegation: this.delegation,
                ownerAspNetUserId: this.aspNetUserId
            }
        });

        dialog
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((delegation: DelegationAddEdit) => {
                if (delegation) {
                    this._store.dispatch(updateDelegation({ delegation }));
                }
            });
    }

    toggleContactInfo() {
        this.isContactInfoHidden = !this.isContactInfoHidden;
    }

    impersonate(impersonatedUserId: string) {
        if (!this.auth0NovaImpersonationSignal()) {
            return;
        }
        this._authService.changeProfile(impersonatedUserId, true);
        this._dialog.closeAll();
    }
}
